import { z } from "zod";
import { AI_PAGE_TIERS, type AiPageTier } from "../registry/schema.js";

/**
 * 🔴 US-AIV2-0012 (@UC0166 · ADR-0009) — TRẠNG THÁI PHIẾU VIỆC: enum ĐÓNG + bảng chuyển ĐÓNG **theo TIER**.
 *
 * US-AIV2-0021: CHUYỂN từ `packages/contracts/src/ai/task-state.ts` về domain (contracts re-export nguyên) — máy trạng thái
 * (`task-machine.ts`) phải THUẦN ở domain, mà domain không import contracts (chiều phụ thuộc contracts → domain). Hành vi giữ nguyên.
 *
 * `idle → collecting → clarifying → confirming → executing → done | failed | cancelled`. Status ngoài enum = lỗi ở biên (zod),
 * chuyển ngoài bảng = lỗi ở writer (`AiTaskAuditWriter` ném, KHÔNG ghi dòng). Mọi chuyển hợp lệ ghi ĐÚNG MỘT dòng `ai_audit_log`.
 *
 * 🔴 Review #804 H1 — bảng phải có CHIỀU TIER, vì luật quan trọng nhất của v2 là luật tier (`@UC0169`: đọc = không phiếu ·
 * T1 = một chạm · T2 = phiếu đầy đủ · redirect = chỉ nút mở màn hình). Bản tier-mù cho `idle → executing` với MỌI tier ⇒ writer
 * sẵn sàng ghi một chuyển trạng thái BỎ QUA phiếu xác nhận cho việc ghi tiền; đột biến "đổi confirming→executing thành
 * collecting→executing" sống qua suite. Nay:
 *  - **T1 / T2** (ghi): `executing` CHỈ tới từ `confirming` (AI-INV-4: mọi write có human-in-the-loop). T1 "một chạm" khác T2 ở
 *    ĐỘ NẶNG của phiếu (0023 render), KHÔNG ở đường đi — cùng bảng.
 *  - **read**: không có `confirming`; `idle → executing` trực tiếp; vẫn có `collecting`/`clarifying` cho ô bắt buộc (vd chỉ số nào).
 *  - **redirect**: không có `executing` — chỉ đưa nút mở màn hình rồi `done`, hoặc `cancelled`.
 *
 * Vì sao bảng là DỮ LIỆU thay vì if/else trong máy trạng thái (0021): 0021 dựng máy; mobile (0024) tô màu theo trạng thái;
 * audit ghi dòng. Ba nơi đọc một bảng, không nơi nào tự viết luật chuyển của riêng nó.
 *
 * Hai luật đáng chú ý:
 *  - **Huỷ là tuyệt đối** (`@UC0164`): mọi trạng thái CHƯA CHẠY → `cancelled`. Riêng `executing` KHÔNG huỷ được — bút toán là
 *    một transaction nguyên tử, "huỷ giữa chừng" không tồn tại; nó chỉ ra `done` hoặc `failed` (`@UC0168`).
 *  - Freeze SÂU (#804 M5): từng mảng đích đều `Object.freeze` — `push` ở runtime ném, không chỉ kiểu `readonly`.
 */
export const AI_TASK_STATES = ["idle", "collecting", "clarifying", "confirming", "executing", "done", "failed", "cancelled"] as const;
export type AiTaskState = (typeof AI_TASK_STATES)[number];
export const aiTaskStateSchema = z.enum(AI_TASK_STATES);

/** Trạng thái kết — không chuyển đi đâu nữa. */
export const AI_TASK_TERMINAL_STATES: readonly AiTaskState[] = Object.freeze(["done", "failed", "cancelled"]);

type TransitionTable = Readonly<Record<AiTaskState, readonly AiTaskState[]>>;

function freezeTable(table: Record<AiTaskState, AiTaskState[]>): TransitionTable {
  for (const key of Object.keys(table) as AiTaskState[]) Object.freeze(table[key]);
  return Object.freeze(table);
}

/** Đường đi của việc GHI (T1/T2): `executing` chỉ sau `confirming`. */
const WRITE_TRANSITIONS: TransitionTable = freezeTable({
  idle: ["collecting", "clarifying", "confirming", "cancelled"],
  collecting: ["clarifying", "confirming", "cancelled", "failed"],
  clarifying: ["collecting", "confirming", "cancelled", "failed"],
  confirming: ["executing", "collecting", "cancelled"],
  executing: ["done", "failed"],
  done: [],
  failed: [],
  cancelled: [],
});

/** Đường đi của việc ĐỌC: không phiếu, không `confirming`. */
const READ_TRANSITIONS: TransitionTable = freezeTable({
  idle: ["collecting", "clarifying", "executing", "cancelled"],
  collecting: ["clarifying", "executing", "cancelled", "failed"],
  clarifying: ["collecting", "executing", "cancelled", "failed"],
  confirming: [],
  executing: ["done", "failed"],
  done: [],
  failed: [],
  cancelled: [],
});

/** Trang `redirect`: chỉ đưa nút mở màn hình — không ô, không phiếu, không chạy gì. */
const REDIRECT_TRANSITIONS: TransitionTable = freezeTable({
  idle: ["done", "cancelled"],
  collecting: [],
  clarifying: [],
  confirming: [],
  executing: [],
  done: [],
  failed: [],
  cancelled: [],
});

/** Bảng chuyển đóng THEO TIER: tier → từ → tập đích hợp lệ. Trạng thái kết ⇒ tập rỗng ở mọi tier. */
export const AI_TASK_TRANSITIONS_BY_TIER: Readonly<Record<AiPageTier, TransitionTable>> = Object.freeze({
  read: READ_TRANSITIONS,
  T1: WRITE_TRANSITIONS,
  T2: WRITE_TRANSITIONS,
  redirect: REDIRECT_TRANSITIONS,
});

/** Chuyển `from → to` có trong bảng của `tier` không. Self-loop KHÔNG phải một chuyển ⇒ false. Tier lạ ⇒ false (fail-closed). */
export function canTransitionAiTask(from: AiTaskState, to: AiTaskState, tier: AiPageTier): boolean {
  if (!AI_PAGE_TIERS.includes(tier)) return false;
  return AI_TASK_TRANSITIONS_BY_TIER[tier][from].includes(to);
}

export function isAiTaskTerminal(state: AiTaskState): boolean {
  return AI_TASK_TERMINAL_STATES.includes(state);
}
